import { Button } from "flowbite-react";
import React, { useState } from "react";
import {
  PaymentType,
  paymentTypeDisplayNames,
} from "@/common/enum/payment-type";
import {
  OrderStatus,
  OrderStatusDisplayNames,
} from "@/common/enum/order-status";
import { ButtonColor } from "@/common/enum/button-color";
import { OrderDetailDto } from "@/types/dto/order-detail-dto";
import { OrderPagePayload } from "@/types/payload/order-page-payload";
import { orderService } from "@/services/order-service2";
import { OrderSortType } from "@/common/enum/partner-manage-order-sort";

interface Props {
  currentFilters: {
    fromDate?: Date;
    toDate?: Date;
    orderType?: OrderSortType;
    keyword?: string;
  };
  fileName?: string;
  disabled?: boolean;
  className?: string;
}

const csvColumns = [
  {
    id: "invoiceNumber",
    Header: "발주번호",
  },
  {
    id: "createdAt",
    Header: "발주 접수 시간",
  },
  { id: "status", Header: "발주 상태" },
  { id: "ordererName", Header: "파트너명" },
  { id: "ordererBusinessName", Header: "상호명" },
  { id: "ordererBusinessNumber", Header: "사업자번호" },
  { id: "ordererPhone", Header: "파트너 연락처" },
  { id: "ordererMail", Header: "파트너 이메일" },
  { id: "originalPriceSum", Header: "정가" },
  { id: "discountPriceSum", Header: "상품 할인" },
  { id: "incentiveDiscountPriceSum", Header: "인센티브 할인" },
  { id: "totalPriceSum", Header: "총 가격" },
  { id: "vatSum", Header: "부가세" },
  { id: "shippingPrice", Header: "배송비" },
  { id: "payment.paymentMethod", Header: "결제 수단" },
  { id: "payment.amount", Header: "결제 금액" },
  { id: "payment.completedAt", Header: "결제 완료 시간" },
  { id: "payment.tno", Header: "결제 거래번호" },
  { id: "receiverName", Header: "발주자명" },
  { id: "noSong", Header: "운송장 번호" },
  { id: "receiverPostalCode", Header: "우편번호" },
  { id: "receiverAddress", Header: "주소" },
  { id: "receiverAddressDetail", Header: "상세 주소" },
  { id: "receiverMemo", Header: "배송 메모" },
];

export const AdminOrderExcelDownloadButton: React.FC<Props> = ({
  currentFilters,
  fileName,
  disabled = false,
  className = "",
}) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const convertToCSV = (rows: OrderDetailDto[]) => {
    // 헤더 행
    const headerRow = csvColumns.map((col) => col.Header).join(",");
    // 데이터 행
    const dataRows = rows.map((row: any) => {
      return csvColumns
        .map((col) => {
          // 발주 상태
          if (col.id === "status") {
            return OrderStatusDisplayNames[row.status as OrderStatus] || "";
          }
          // 결제 수단
          if (col.id === "payment.paymentMethod") {
            if (!row.payment?.paymentMethod) {
              return "";
            }
            return (
              paymentTypeDisplayNames[
                row.payment.paymentMethod as PaymentType
              ] || ""
            );
          }

          // 결제거래번호 문자열로
          if (col.id === "payment.tno") {
            return `${row.payment?.tno || ""}`;
          }

          if (col.id === "payment.amount") {
            return row.payment?.amount || "";
          }

          if (col.id === "payment.completedAt") {
            return row.payment?.completedAt || "";
          }

          const cellData = row[col.id];

          return typeof cellData === "string" && cellData.includes(",")
            ? `"${cellData}"`
            : cellData || "";
        })
        .join(",");
    });

    return [headerRow, ...dataRows].join("\n");
  };

  const downloadCSV = (csvContent: string, fileName: string) => {
    const blob = new Blob(["\ufeff" + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const fetchAllData = async (): Promise<OrderDetailDto[]> => {
    const payload: OrderPagePayload = {
      page: 1,
      size: 9999, // 충분히 큰 사이즈로 모든 데이터 조회
      fromDate: currentFilters.fromDate,
      toDate: currentFilters.toDate,
      orderType: currentFilters.orderType,
      keyword: currentFilters.keyword,
      needTrackingNumber: true,
    };

    const response = await orderService.getPage(payload);
    if (!response.ok) {
      throw new Error("데이터 조회에 실패했습니다.");
    }

    return response.data.contents;
  };

  const handleExcelDownload = async () => {
    setIsDownloading(true);

    try {
      const allData = await fetchAllData();

      if (allData.length === 0) {
        alert("다운로드할 데이터가 없습니다.");
        return;
      }

      const csvContent = convertToCSV(allData);
      const defaultFileName =
        fileName ||
        `${new Date()
          .toLocaleDateString("ko-KR", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          })
          .replace(/\. /g, ".")
          .replace(/\.$/, "")} 발주내역.csv`;

      downloadCSV(csvContent, defaultFileName);
    } catch (error) {
      console.error("다운로드 중 오류가 발생했습니다:", error);
      alert("다운로드 중 오류가 발생했습니다.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      className={`bg-white border border-green-500 ${className}`}
      color={ButtonColor.GREEN}
      onClick={handleExcelDownload}
      disabled={disabled || isDownloading}
    >
      <img
        src="/svg/Office-Logos.svg"
        alt="Excel Icon"
        className="w-5 h-5 mr-2"
      />
      <span>
        {isDownloading
          ? "데이터를 불러오고 있습니다..."
          : "발주내역 Excel 다운로드"}
      </span>
    </Button>
  );
};
